import { useCallback } from 'react';
import {
  Note,
  Evaluation,
} from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import { useUpdateNoteWithHistory } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/evaluations/useUpdateNote';
import { useCDNContext } from '@teetsh/app/src/containers/CarnetsDeNotes/CarnetDeNotesContext';
import { useUpdateEvaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/evaluations/useUpdateEvaluation';

/**
 * Hook that ensures when updating one note in a multi-skill evaluation,
 * all the student's notes (including temporary ones) are persisted.
 *
 * ## Problem this solves:
 * In multi-skill evaluations, when a new student is added, they receive temporary (non-persisted)
 * notes for each skill. These notes exist only in memory until they are saved. When a teacher
 * grades ONE skill, only that specific note gets persisted to the database, causing the other
 * skill lines to disappear from the UI.
 *
 * The temporary notes are created in:
 * - `getNotesOfElevesFromEvaluationOrCreateOne` (lines 37-47): Creates notes matching other students' structure
 * - `fillNotesWithMissingEleves` (lines 43-49): Creates notes for each referential item
 * - Called from `Notes.tsx` (line 108): When rendering each student's notes
 *
 * ## Example scenario:
 * 1. Teacher creates evaluation with 3 skills: "Grammar", "Spelling", "Vocabulary"
 * 2. New student "John" is added → gets 3 temporary note objects (one per skill)
 * 3. Teacher grades John's "Grammar" as "A" → only Grammar note is persisted
 * 4. UI re-renders → "Spelling" and "Vocabulary" lines disappear (still temporary)
 *
 * ## How this hook solves it:
 * When updating any note for a student, this hook:
 * 1. Updates the specific note that was graded (normal behavior)
 * 2. Checks if the student has temporary notes not yet persisted
 * 3. If yes, persists ALL the student's notes at once
 * 4. This ensures all skill lines remain visible after grading any single skill
 *
 * ## When to use:
 * - Use this hook for multi-skill evaluations (when notes.length > 1 or has referentialItemId)
 * - Use regular updateNote for single-skill evaluations
 */
export function useUpdateNoteWithAllSkills(
  evaluationId: string,
  eleveId: string,
  notes: Note[],
  evaluation: Evaluation,
) {
  const { cdnId, notationSystem } = useCDNContext();
  const updateNote = useUpdateNoteWithHistory(
    {
      id: cdnId,
      minValue: notationSystem.minValue,
      maxValue: notationSystem.maxValue,
    },
    evaluationId,
  );
  const { update: updateEvaluation } = useUpdateEvaluation(cdnId, evaluationId);

  const updateNoteAndEnsureAllPersisted = useCallback(
    async (noteToUpdate: Note) => {
      // First update the specific note
      await updateNote(noteToUpdate);

      // Check if this student has all their notes persisted
      const studentNotes = notes.filter(n => n.eleveId === eleveId);
      const persistedStudentNotes = evaluation.notes.filter(
        n => n.eleveId === eleveId,
      );

      // If we have temporary notes that aren't persisted yet
      if (studentNotes.length > persistedStudentNotes.length) {
        // Find notes that need to be added
        const persistedNoteIds = new Set(persistedStudentNotes.map(n => n.id));
        const notesToAdd = studentNotes.filter(
          n => !persistedNoteIds.has(n.id),
        );

        // Update the evaluation with all notes including the new ones
        const updatedNotes = [...evaluation.notes, ...notesToAdd].map(n => ({
          ...n,
          notationSystemMax: notationSystem.maxValue,
          notationSystemMin: notationSystem.minValue,
        }));
        await updateEvaluation({ ...evaluation, notes: updatedNotes });
      }
    },
    [updateNote, notes, eleveId, evaluation, updateEvaluation],
  );

  return updateNoteAndEnsureAllPersisted;
}
